import {Segment, showNotification} from 'platform/components';
import {HStack, Box, Heading, VStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';

import {mergeAll} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {usePostServiceCaseCopyServiceRequestsMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {
  workshopSearchParams,
  queryParams,
  ORDER_TABS,
  handleApiError,
  DmsDataGrid,
  refreshDmsDatagrid,
} from '@dms/shared';

import {composePath} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';

interface HistoryOfRepairsProps {
  vehicleId: string;
  onBack: () => void;
}

const PREDEFINED_COLUMNS = ['serviceOrderId', 'serviceCaseId'] as const;

export function HistoryOfRepairs(props: HistoryOfRepairsProps) {
  const {serviceCaseId} = useWorkshopUrl();
  const [postServiceCaseCopyServiceRequests] = usePostServiceCaseCopyServiceRequestsMutation();

  const [activeSegment, setActiveSegment] = useState<string>('serviceRequests');

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {vehicleId: props.vehicleId}]),
    [props.vehicleId]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const serviceOrderId = getColumnData<string>('serviceOrderId')?.value;
    const dgServiceCaseId = getColumnData<string>('serviceCaseId')?.value;

    const requestId = isArray(rowId) ? rowId[0] : rowId;

    const path = composePath(workshopRoutes.serviceCaseDetail, {
      params: {
        id: dgServiceCaseId,
      },
      queryParams: {
        source: 'service-orders',
        section: workshopSearchParams.serviceDetail.orders,
        [queryParams.SERVICE_CASE_ORDER_ID]: serviceOrderId,
        [queryParams.SERVICE_CASE_ORDER_TAB]: ORDER_TABS.JOBS,
        [queryParams.SERVICE_CASE_OPEN_JOB_ID]: requestId,
      },
    });

    match(actionKey)
      .with('open', () => window.open(path))
      .with('copy', () => {
        const serviceJobIds = isArray(rowId) ? rowId : [rowId];
        if (!serviceCaseId || isNilOrEmpty(serviceJobIds)) {
          showNotification.error(i18n.t('entity.orderRequest.labels.copyFailed'));
          return;
        }

        postServiceCaseCopyServiceRequests({
          serviceCaseId,
          body: {
            serviceJobIds,
          },
        })
          .unwrap()
          .then(() => {
            refreshDmsDatagrid('service-history-requests-service-case-creating');
          })
          .catch(handleApiError);
      })
      .otherwise(() =>
        showNotification.error(`Action callback was not specified for action ${actionKey}`)
      );
  };

  return (
    <Box width="100%">
      <VStack spacing={4}>
        <HStack width="100%" justify="space-between">
          <Heading size={4}>
            {activeSegment === 'serviceRequests'
              ? i18n.t('page.workshop.labels.serviceRequests')
              : i18n.t('entity.orderRequest.labels.workItems')}
          </Heading>

          <Segment
            minWidth={31}
            value={activeSegment}
            onChange={setActiveSegment}
            options={[
              {value: 'serviceRequests', label: i18n.t('page.workshop.labels.serviceRequests')},
              {value: 'workItems', label: i18n.t('entity.orderRequest.labels.workItems')},
            ]}
          />
        </HStack>

        <Box height={170}>
          <Show when={activeSegment === 'serviceRequests'}>
            <DmsDataGrid
              gridCode="service-history-requests-service-case-creating"
              queryModifier={queryModifier}
              actionCallback={actionCallback}
              predefinedColumns={PREDEFINED_COLUMNS}
            />
          </Show>

          <Show when={activeSegment === 'workItems'}>
            <DmsDataGrid
              gridCode="service-history-order-items-for-case-creating"
              queryModifier={queryModifier}
            />
          </Show>
        </Box>
      </VStack>
    </Box>
  );
}
